import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAreaOfInterest, selectSportsType } from "../selectors";
import { dayToNumber } from "../config/utils";
import {
  fetchLeagueMatchesOfDay,
  fetchLeagueMatchesOfDayLeague,
} from "../store/actions/matchActions";
import { performAction } from "../store/actions/all";

const NO_LEAGUE_ID = -1;

type UseMatchesFetcherParams = {
  selectedDate: Date;
  urlDate?: string;
  realLeagueId: number;
  isHomePage: boolean;
};

const isValidDateString = (value?: string): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const parseUrlDate = (value?: string): Date | undefined => {
  if (!isValidDateString(value)) {
    return undefined;
  }
  const [year, month, day] = value.split("-").map((part) => parseInt(part, 10));
  const parsedDate = new Date(year, month - 1, day);
  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

export const useMatchesFetcher = ({
  selectedDate,
  urlDate,
  realLeagueId,
  isHomePage,
}: UseMatchesFetcherParams): void => {
  const dispatch = useDispatch();
  const sports = useSelector(selectSportsType);
  const aoiId = useSelector(selectAreaOfInterest)?.id;

  const fetchMatches = useCallback(
    (date: Date) => {
      if (realLeagueId === NO_LEAGUE_ID) {
        if (isHomePage && aoiId != null) {
          performAction({
            f: fetchLeagueMatchesOfDay,
            p: [dispatch, aoiId, dayToNumber(date), sports],
          });
        }
        return;
      }

      performAction({
        f: fetchLeagueMatchesOfDayLeague,
        p: [dispatch, dayToNumber(date), realLeagueId],
      });
    },
    [dispatch, aoiId, sports, isHomePage, realLeagueId]
  );

  useEffect(() => {
    const parsedUrlDate = isHomePage ? parseUrlDate(urlDate) : undefined;
    const dateToUse = parsedUrlDate ?? selectedDate;
    fetchMatches(dateToUse);
  }, [fetchMatches, selectedDate, urlDate, isHomePage]);
};

